import { AcademicianButton } from "@/components/academician/academician-button"
import { ArrowRightIcon } from "lucide-react"

interface HeroSectionProps {
  /**
   * Main headline text
   */
  headline?: string;

  /**
   * Subheadline text
   */
  subheadline?: string;

  /**
   * Primary CTA button text
   */
  primaryCtaText?: string;

  /**
   * Primary CTA button click handler
   */
  onPrimaryCtaClick?: () => void;

  /**
   * Secondary CTA button text
   */
  secondaryCtaText?: string;

  /**
   * Secondary CTA button click handler
   */
  onSecondaryCtaClick?: () => void;

  /**
   * Hero image URL
   */
  heroImage?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export function HeroSection({
  headline = "Master Customer Discovery",
  subheadline = "Join a cohort of founders learning to validate startup ideas",
  primaryCtaText = "View Cohorts",
  onPrimaryCtaClick,
  secondaryCtaText = "Learn More",
  onSecondaryCtaClick,
  heroImage,
  className = ""
}: HeroSectionProps) {
  return (
    <section className={`bg-background py-16 md:py-24 lg:py-32 ${className}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                {headline}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
                {subheadline}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <AcademicianButton
                variant="primary"
                size="lg"
                rightIcon={<ArrowRightIcon />}
                onClick={onPrimaryCtaClick}
              >
                {primaryCtaText}
              </AcademicianButton>
              <AcademicianButton
                variant="secondary"
                size="lg"
                onClick={onSecondaryCtaClick}
              >
                {secondaryCtaText}
              </AcademicianButton>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-2">
                {[
                  "https://github.com/yusufhilmi.png",
                  "https://github.com/kdrnp.png",
                  "https://github.com/yahyabedirhan.png",
                  "https://github.com/denizbuyuktas.png"
                ].map((avatar, index) => (
                  <img
                    key={index}
                    src={avatar}
                    alt={`Founder ${index + 1}`}
                    className="w-10 h-10 rounded-full border-2 border-background"
                  />
                ))}
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">500+</span> founders already learning
              </div>
            </div>
          </div>

          {/* Right Column - Hero Image */}
          <div className="relative">
            <img
              src={heroImage || "/hero-dashboard.png"}
              alt="Academician Startup founder dashboard"
              className="w-full h-auto rounded-lg shadow-xl border border-border"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
